import {
  ActionActor,
  IAutomationActionTemplateAtlassianCreateIssue,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  AtlassianFieldInfo,
  AtlassianFieldValue,
  AtlassianIssueType,
  AtlassianProject
} from '@dadohr/shared/domainTypes/integrations/atlassian';
import {
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { findIndex, isEqual, partition, sortBy } from 'lodash';
import { PlusCircle, X } from 'lucide-react';
import React, { SetStateAction, useEffect, useState } from 'react';
import { AlertBox } from '../../../../../../../components/AlertBox';
import {
  AutocompleteSingle,
  AutocompleteSingleFreeSolo
} from '../../../../../../../components/Autocomplete';
import { CustomInputLabel } from '../../../../../../../components/CustomInputLabel';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../../components/Loader';
import { TokenDefinition } from '../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../components/SelectCustom';
import { SomethingWentWrong } from '../../../../../../../components/SomethingWentWrong';
import { TextFieldWithReplacementTokens } from '../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { useMappedLoadingValue } from '../../../../../../../services/firebase';
import { humanizeDuration } from '../../../../../../../services/time';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import {
  isSupportedField,
  useAtlassianIssueTypes,
  useAtlassianProjects
} from '../../../../../../Org/pages/Integrations/integrations/atlassian/service';
import { Duration } from '../../../TaskBuilder/components/TimeSelectors';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import {
  ActionActorSelectorMulti,
  ActionActorSelectorSingle,
  actionActorToString
} from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_ATLASSIAN_CREATE_ISSUE
} from '../../validation';

const IssueTypePicker = ({
  disabled,
  error,
  helperText,
  orgId,
  options,
  value,
  onBlur,
  onChange
}: {
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  orgId: string;
  options: AtlassianIssueType[];
  value: AtlassianIssueType;
  onBlur?: () => void;
  onChange: (nextValue: AtlassianIssueType) => void;
}) => {
  const { t } = useUnsafeTranslation();
  // The value.fields prop might be outdated,
  // so let’s update those to make sure new required fields are shown, too.
  useEffect(() => {
    // When there are no options, there’s nothing to update
    if (!options) return;

    const optionNew = options.find((o) => o.id === value.id);
    // The issue type no longer exists on Jira — we could fire an event for this
    if (!optionNew) return;
    // The issue type is unchanged, so don’t update
    if (isEqual(optionNew, value)) return;

    onChange(optionNew);
  }, [options, onChange, value]);

  return (
    <AutocompleteSingle
      disabled={disabled}
      value={value.id ? value : null}
      onBlur={onBlur}
      onChange={(newValue) => newValue && onChange(newValue)}
      options={options}
      getOptionLabel={(o) => o.name}
      error={error}
      helperText={helperText}
      filterOptions={(is, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return is.filter((p) => p.name.match(t));
      }}
      disabledClearable
      fullWidth
      label={t('Issue type')}
    />
  );
};

const ProjectPicker = ({
  error,
  helperText,
  orgId,
  projects,
  value,
  onBlur,
  onChange
}: {
  error?: boolean;
  helperText?: React.ReactNode;
  orgId: string;
  projects: AtlassianProject[];
  value: AtlassianProject;
  onBlur?: () => void;
  onChange: (nextValue: AtlassianProject) => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <AutocompleteSingle
      value={value.key ? value : null}
      onBlur={onBlur}
      onChange={(newValue) => newValue && onChange(newValue)}
      options={projects}
      getOptionLabel={(o) => o.name}
      error={error}
      helperText={helperText}
      filterOptions={(ps, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return ps.filter((p) => p.name.match(t));
      }}
      disabledClearable
      fullWidth
      label={t('Project')}
    />
  );
};

const getDefaultFieldValueByType = (
  fieldInfo: AtlassianFieldInfo
): AtlassianFieldValue => {
  switch (fieldInfo.type) {
    case 'DATETIME':
      return {
        key: fieldInfo.key,
        value: '',
        mode: 'absolute',
        type: 'DATETIME'
      };
    case 'OPTION':
      return {
        key: fieldInfo.key,
        value: null,
        type: 'OPTION'
      };
    case 'OPTION_LIST':
      return {
        key: fieldInfo.key,
        value: '',
        type: 'OPTION_LIST'
      };
    case 'STRING':
      return {
        key: fieldInfo.key,
        value: '',
        type: 'STRING',
        multiline:
          fieldInfo.schema.custom ===
          'com.atlassian.jira.plugin.system.customfieldtypes:textarea'
      };
    case 'STRING_LIST':
      return {
        key: fieldInfo.key,
        value: '',
        type: 'STRING_LIST'
      };
    case 'USERLIST':
      return {
        key: fieldInfo.key,
        value: [],
        type: 'USERLIST'
      };
    case 'REQUESTTYPE':
      return {
        key: fieldInfo.key,
        value: null,
        type: 'REQUESTTYPE'
      };
    case 'USER':
      return {
        key: fieldInfo.key,
        value: null,
        type: 'USER'
      };
    default:
      return assertNever(fieldInfo);
  }
};

const AtlassianCustomField = ({
  error,
  expTpl,
  fieldInfo,
  helperText,
  issueTypeId,
  value,
  onBlur,
  onChange,
  tokens
}: {
  error: boolean;
  expTpl: TaskBuilderContext;
  fieldInfo: AtlassianFieldInfo;
  helperText?: React.ReactNode;
  issueTypeId: string;
  value: AtlassianFieldValue | null;
  onBlur: () => any;
  onChange: (nextValue: AtlassianFieldValue) => any;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const actualValue =
    value?.type === fieldInfo.type
      ? value
      : getDefaultFieldValueByType(fieldInfo);

  if (fieldInfo.type === 'DATETIME' && actualValue.type === 'DATETIME') {
    if (actualValue.mode === 'absolute') {
      return (
        <TextFieldWithReplacementTokens
          fullWidth
          error={error}
          helperText={helperText}
          label={fieldInfo.name}
          onChange={(v) =>
            onChange({
              ...actualValue,
              value: v
            })
          }
          onBlur={onBlur}
          required={fieldInfo.required}
          value={actualValue.value}
          tokens={tokens}
        />
      );
    } else {
      return (
        <FormControl>
          <CustomInputLabel label={fieldInfo.name} variant="secondary">
            <Duration
              error={error}
              helperText={helperText}
              value={actualValue.value}
              onBlur={onBlur}
              onChange={(nextValue) => {
                onChange({ ...actualValue, value: nextValue });
              }}
            />
          </CustomInputLabel>
        </FormControl>
      );
    }
  }
  if (fieldInfo.type === 'REQUESTTYPE' && actualValue.type === 'REQUESTTYPE') {
    const fieldOptions = sortBy(
      fieldInfo.allowedValues.filter(
        (item) => item.issueTypeId === issueTypeId
      ),
      (item) => item.value
    );
    if (!fieldOptions.length) {
      return null;
    }
    return (
      <SelectCustom
        error={error}
        helperText={helperText}
        label={fieldInfo.name}
        value={actualValue.value}
        fullWidth
        required={fieldInfo.required}
        onChange={(event) =>
          onChange({
            ...actualValue,
            value: event.target.value as string
          })
        }
        onBlur={onBlur}
      >
        {fieldOptions.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.value}
          </MenuItem>
        ))}
      </SelectCustom>
    );
  }
  if (fieldInfo.type === 'STRING' && actualValue.type === 'STRING') {
    return (
      <TextFieldWithReplacementTokens
        fullWidth
        error={error}
        helperText={helperText}
        label={fieldInfo.name}
        onChange={(v) =>
          onChange({
            ...actualValue,
            value: v
          })
        }
        onBlur={onBlur}
        required={fieldInfo.required}
        value={actualValue.value}
        tokens={tokens}
      />
    );
  }

  if (fieldInfo.type === 'STRING_LIST' && actualValue.type === 'STRING_LIST') {
    return (
      <TextFieldWithReplacementTokens
        fullWidth
        error={error}
        helperText={t('Comma-separated list of values') || helperText}
        label={fieldInfo.name}
        onChange={(v) =>
          onChange({
            ...actualValue,
            value: v
          })
        }
        onBlur={onBlur}
        required={fieldInfo.required}
        value={actualValue.value}
        tokens={tokens}
      />
    );
  }

  if (fieldInfo.type === 'OPTION_LIST' && actualValue.type === 'OPTION_LIST') {
    return (
      <TextFieldWithReplacementTokens
        fullWidth
        error={error}
        helperText={t('Comma-separated list of values') || helperText}
        label={fieldInfo.name}
        onChange={(v) =>
          onChange({
            ...actualValue,
            value: v
          })
        }
        onBlur={onBlur}
        required={fieldInfo.required}
        value={actualValue.value}
        tokens={tokens}
      />
    );
  }
  if (fieldInfo.type === 'OPTION' && actualValue.type === 'OPTION') {
    // TODO - replacement token picker would be nice here
    return (
      <AutocompleteSingleFreeSolo
        label={fieldInfo.name}
        error={error}
        helperText={helperText}
        fullWidth
        required={fieldInfo.required}
        onBlur={onBlur}
        options={fieldInfo.allowedValues.map((o) => o.value)}
        value={actualValue.value || ''}
        onChange={(v) =>
          onChange({
            ...actualValue,
            value: v
          })
        }
      />
    );
    // return (
    //   <SelectCustom
    //     error={error}
    //     helperText={helperText}
    //     label={fieldInfo.name}
    //     value={actualValue.value}
    //     fullWidth
    //     required={fieldInfo.required}
    //     onChange={(event) =>
    //       onChange({
    //         ...actualValue,
    //         value: event.target.value as string
    //       })
    //     }
    //     onBlur={onBlur}
    //   >
    //     {fieldInfo.allowedValues.map((item) => (
    //       <MenuItem key={item.id} value={item.id}>
    //         {item.value}
    //       </MenuItem>
    //     ))}
    //   </SelectCustom>
    // );
  }
  if (fieldInfo.type === 'USERLIST' && actualValue.type === 'USERLIST') {
    return (
      <ActionActorSelectorMulti
        label={fieldInfo.name}
        value={actualValue.value}
        onChange={(nextActors) =>
          onChange({
            ...actualValue,
            value: nextActors
          })
        }
        expTpl={expTpl}
        fullWidth
      />
    );
  }

  if (fieldInfo.type === 'USER' && actualValue.type === 'USER') {
    return (
      <ActionActorSelectorSingle
        label={fieldInfo.name}
        value={actualValue.value}
        onChange={(nextActor) =>
          onChange({
            ...actualValue,
            value: nextActor
          })
        }
        expTpl={expTpl}
        fullWidth
      />
    );
  }

  // type mismatch - this can never happen
  return null;
};

export const ActionSettingsAtlassianCreateIssueInner = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation,
  tokens
}: {
  value: IAutomationActionTemplateAtlassianCreateIssue;
  onChange: (nextValue: IAutomationActionTemplateAtlassianCreateIssue) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();

  const [showOtherFieldSelector, setShowOtherFieldSelector] = useState(false);

  const [
    options,
    loading,
    error
  ] = useMappedLoadingValue(
    useAtlassianIssueTypes(expTpl.orgId, value.project.id, expTpl.templateId),
    (os) => sortBy(os, (o) => o.name)
  );

  const specialImplementationFields = ['summary', 'description', 'reporter'];

  const [requiredCustomFields, otherFields] = partition(
    value.issueType.fields.filter(
      (f) => !specialImplementationFields.includes(f.key)
    ),
    (f) => isSupportedField(f)
  );

  const [usedFields, unusedFields] = partition(
    value.issueType.fields,
    (f) => !!value.fields.find((of) => of.key === f.key)
  );
  const usedOtherFields = sortBy(
    usedFields.filter(
      (f) =>
        !specialImplementationFields.includes(f.key) &&
        !!otherFields.find((of) => of.key === f.key)
    ),
    (x) => findIndex(value.fields, (a) => a.key === x.key)
  );
  const unusedOtherFields = sortBy(
    unusedFields.filter(
      (f) =>
        // For fields that were just added and a selection needs to be made for which field it is
        f.key === '' ||
        (!specialImplementationFields.includes(f.key) &&
          !!otherFields.find((of) => of.key === f.key))
    ),
    (x) => x.name
  );

  const validateIssueType = useFieldValidator(
    setValidation,
    VALIDATORS_ATLASSIAN_CREATE_ISSUE,
    'issueType'
  );
  const invalidIssueType = validation?.fields.issueType || null;

  const validateSummary = useFieldValidator(
    setValidation,
    VALIDATORS_ATLASSIAN_CREATE_ISSUE,
    'summary'
  );
  const invalidSummary = validation?.fields.summary || null;

  const validateCustomFields = useFieldValidator(
    setValidation,
    VALIDATORS_ATLASSIAN_CREATE_ISSUE,
    'fields'
  );
  const invalidCustomFields: { [fieldKey: string]: string } | null = validation
    ?.fields.fields
    ? JSON.parse(validation.fields.fields)
    : null;

  if (loading) {
    return <Loader height={300} />;
  }

  if (error || !options) {
    return <SomethingWentWrong />;
  }

  return (
    <React.Fragment key={value.project.id}>
      <IssueTypePicker
        key={value.project.id || 'none'}
        disabled={!value.project.id}
        error={!!invalidIssueType}
        helperText={invalidIssueType}
        orgId={expTpl.orgId}
        options={options}
        value={value.issueType}
        onBlur={() => validateIssueType(value, expTpl, aTpl)}
        onChange={(nextIssueType) => {
          const nextValue: IAutomationActionTemplateAtlassianCreateIssue = {
            ...value,
            issueType: nextIssueType,
            fields:
              value.issueType?.id === nextIssueType?.id ? value.fields : []
          };
          invalidIssueType && validateIssueType(nextValue, expTpl, aTpl);
          return onChange(nextValue);
        }}
      />

      {value.issueType.id && (
        <FlexContainerVertical
          fullWidth
          spacing={3}
          key={value.issueType.id}
          marginTop={1}
        >
          <FlexContainerVertical fullWidth spacing={2}>
            <Typography variant="h6">{t('Required Fields')}</Typography>
            <TextFieldWithReplacementTokens
              label={t('Summary')}
              fullWidth
              variant="outlined"
              error={!!invalidSummary}
              helperText={invalidSummary}
              value={value.summary}
              onChange={(nextSummary) => {
                const nextValue = {
                  ...value,
                  summary: nextSummary
                };
                invalidSummary && validateSummary(nextValue, expTpl, aTpl);
                onChange(nextValue);
              }}
              tokens={tokens}
            />
            <TextFieldWithReplacementTokens
              label={t('Description')}
              multiline
              rows={4}
              value={value.description}
              onChange={(nextDescription) => {
                const nextValue = {
                  ...value,
                  description: nextDescription
                };
                onChange(nextValue);
              }}
              tokens={tokens}
              fullWidth
            />
            <ActionActorSelectorSingle
              label={t('Reporter')}
              value={value.reporter}
              onChange={(nextReporter) =>
                onChange({
                  ...value,
                  reporter: nextReporter
                })
              }
              expTpl={expTpl}
              fullWidth
            />

            {requiredCustomFields.map((fieldInfo) => (
              <AtlassianCustomField
                issueTypeId={value.issueType.id}
                expTpl={expTpl}
                error={!!invalidCustomFields?.[fieldInfo.key]}
                fieldInfo={fieldInfo}
                helperText={invalidCustomFields?.[fieldInfo.key]}
                onBlur={() => validateCustomFields(value, expTpl, aTpl)}
                onChange={(newValue: AtlassianFieldValue) => {
                  const nextFields = value.fields.slice();
                  const i = nextFields.findIndex(
                    (field) => newValue.key === field.key
                  );
                  if (i === -1) {
                    nextFields.push(newValue);
                  } else {
                    nextFields[i] = newValue;
                  }
                  const nextValue = {
                    ...value,
                    fields: nextFields
                  };
                  invalidCustomFields &&
                    validateCustomFields(nextValue, expTpl, aTpl);

                  onChange(nextValue);
                }}
                value={
                  value.fields.find((f) => f.key === fieldInfo.key) || null
                }
                tokens={tokens}
              />
            ))}
          </FlexContainerVertical>

          {!!otherFields.length && (
            <FlexContainerVertical fullWidth spacing={2}>
              <Typography variant="h6">{t('Optional Fields')}</Typography>

              {usedOtherFields.map((fieldInfo) => (
                <FlexContainer
                  fullWidth
                  key={fieldInfo.key}
                  alignItems="flex-start"
                >
                  <AtlassianCustomField
                    issueTypeId={value.issueType.id}
                    expTpl={expTpl}
                    error={!!invalidCustomFields?.[fieldInfo.key]}
                    fieldInfo={fieldInfo}
                    helperText={invalidCustomFields?.[fieldInfo.key]}
                    onBlur={() => validateCustomFields(value, expTpl, aTpl)}
                    onChange={(newValue: AtlassianFieldValue) => {
                      const nextFields = value.fields.slice();
                      const i = nextFields.findIndex(
                        (field) => newValue.key === field.key
                      );
                      if (i === -1) {
                        nextFields.push(newValue);
                      } else {
                        nextFields[i] = newValue;
                      }
                      const nextValue = {
                        ...value,
                        fields: nextFields
                      };
                      invalidCustomFields &&
                        validateCustomFields(nextValue, expTpl, aTpl);

                      onChange(nextValue);
                    }}
                    value={
                      value.fields.find((f) => f.key === fieldInfo.key) || null
                    }
                    tokens={tokens}
                  />
                  <IconButton
                    style={{ marginTop: 12 }}
                    onClick={() => {
                      onChange({
                        ...value,
                        fields: value.fields.filter(
                          (f) => f.key !== fieldInfo.key
                        )
                      });
                    }}
                  >
                    <X size={20} />
                  </IconButton>
                </FlexContainer>
              ))}

              {showOtherFieldSelector && (
                <AutocompleteSingle
                  label={t('Select a field')}
                  value={null}
                  options={unusedOtherFields}
                  onChange={(o) => {
                    if (o) {
                      onChange({
                        ...value,
                        fields: [...value.fields, getDefaultFieldValueByType(o)]
                      });
                      setShowOtherFieldSelector(false);
                    }
                  }}
                  getOptionLabel={(o) => o.name}
                  renderOption={(o) => o.name}
                  fullWidth
                  autoFocus
                />
              )}

              {!!unusedOtherFields.length && (
                <Button
                  size="small"
                  color="primary"
                  startIcon={<PlusCircle size={20} />}
                  onClick={() => {
                    if (unusedOtherFields.length === 1) {
                      onChange({
                        ...value,
                        fields: [
                          ...value.fields,
                          getDefaultFieldValueByType(unusedOtherFields[0])
                        ]
                      });
                    } else {
                      setShowOtherFieldSelector(true);
                    }
                  }}
                >
                  {t('Add Field')}
                </Button>
              )}
            </FlexContainerVertical>
          )}

          <AlertBox color="blue">
            Can't find the field you're looking for? Contact{' '}
            <a href="mailto:help@dadohr.com">help@dadohr.com</a> to request an
            update to your integration.
          </AlertBox>
        </FlexContainerVertical>
      )}
    </React.Fragment>
  );
};

export const ActionSettingsAtlassianCreateIssue = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation,
  tokens
}: {
  value: IAutomationActionTemplateAtlassianCreateIssue;
  onChange: (nextValue: IAutomationActionTemplateAtlassianCreateIssue) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  tokens: TokenDefinition[];
}) => {
  const validateProject = useFieldValidator(
    setValidation,
    VALIDATORS_ATLASSIAN_CREATE_ISSUE,
    'project'
  );
  const invalidProject = validation?.fields.project || null;

  const [projects, loading, projectsLoadError] = useAtlassianProjects(
    expTpl.orgId
  );

  if (loading) {
    return <Loader />;
  }

  if (projectsLoadError || !projects) {
    console.error(projectsLoadError);
    return <SomethingWentWrong />;
  }

  return (
    <FlexContainerVertical spacing={2} fullWidth>
      <ProjectPicker
        error={!!invalidProject}
        helperText={invalidProject}
        orgId={expTpl.orgId}
        projects={projects}
        value={value.project}
        onBlur={() => validateProject(value, expTpl, aTpl)}
        onChange={(nextProject) => {
          const nextValue: IAutomationActionTemplateAtlassianCreateIssue = {
            ...value,
            project: nextProject,
            issueType: { id: '', name: '', fields: [] }
          };
          invalidProject && validateProject(nextValue, expTpl, aTpl);
          return onChange(nextValue);
        }}
      />
      {value.project.id && (
        <ActionSettingsAtlassianCreateIssueInner
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          aTpl={aTpl}
          validation={validation}
          setValidation={setValidation}
          tokens={tokens}
        />
      )}
    </FlexContainerVertical>
  );
};

export const ActionSettingsAtlassianCreateIssueReadOnly = ({
  expTpl,
  value
}: {
  expTpl: TaskBuilderContext;
  value: IAutomationActionTemplateAtlassianCreateIssue;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <>
      <FormRow>
        <DataFieldWithLabel label={t('Project')}>
          {value.project.name}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={t('Issue type')}>
          {value.issueType.name}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={t('Summary')}>
          {value.summary}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={t('Reporter')}>
          {value.reporter
            ? actionActorToString(value.reporter, expTpl.supportingActors, t)
            : '-'}
        </DataFieldWithLabel>
      </FormRow>
      <FormRow>
        <DataFieldWithLabel label={t('Description')}>
          {value.description}
        </DataFieldWithLabel>
      </FormRow>
      {value.fields.map((field) => {
        const fieldInfo = value.issueType.fields.find(
          (f) => f.key === field.key
        );

        return (
          <FormRow key={field.key}>
            <DataFieldWithLabel label={fieldInfo?.name || field.key}>
              {fieldInfo?.type === 'DATETIME' &&
              field.type === 'DATETIME' &&
              field.mode === 'relative'
                ? `${humanizeDuration(
                    field.value as number
                  )} after this automation runs`
                : fieldInfo?.type === 'DATETIME' &&
                  field.type === 'DATETIME' &&
                  field.mode === 'absolute'
                ? field.value
                : fieldInfo?.type === 'OPTION' && field.type === 'OPTION'
                ? field.value
                : fieldInfo?.type === 'REQUESTTYPE' &&
                  field.type === 'REQUESTTYPE'
                ? field.value // TODO - this is just a plain number... should have a better representation here!
                : fieldInfo?.type === 'USERLIST' && field.type === 'USERLIST'
                ? joinWithCommaAndAnd(
                    (field.value as ActionActor[]).map((a) =>
                      actionActorToString(a, expTpl.supportingActors, t)
                    ),
                    t
                  )
                : fieldInfo?.type === 'USER' && field.type === 'USER'
                ? field.value
                  ? actionActorToString(field.value, expTpl.supportingActors, t)
                  : null
                : fieldInfo?.type === 'OPTION_LIST' &&
                  field.type === 'OPTION_LIST'
                ? field.value
                : fieldInfo?.type === 'STRING_LIST' &&
                  field.type === 'STRING_LIST'
                ? field.value
                : fieldInfo?.type === 'STRING' && field.type === 'STRING'
                ? field.value
                : null}
            </DataFieldWithLabel>
          </FormRow>
        );
      })}
    </>
  );
};
